import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  FileText, Download, Eye, Loader2, FolderOpen, CalendarClock,
  CheckCircle2, ShieldCheck,
} from "lucide-react";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const TYPE_META = {
  vacation_advance: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-700",
    downloadPath: (id, token) =>
      `${API}/employee/vacation-advances/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  vacation_notice: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-700",
    // Default to "recibo" part; user can also fetch "aviso" if needed
    downloadPath: (id, token) =>
      `${API}/employee/vacation-notices/${id}/pdf?token=${encodeURIComponent(token)}&part=recibo`,
  },
  admission_kit: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-700",
    downloadPath: (id, token) =>
      `${API}/employee/admission-kits/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  contract: {
    color: "bg-sky-100 text-sky-800 border-sky-200",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-700",
    downloadPath: (id, token) =>
      `${API}/employee/contracts/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  epi: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-700",
    downloadPath: (id, token) =>
      `${API}/employee/epi-records/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  badge: {
    color: "bg-sky-100 text-sky-800 border-sky-200",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-700",
    downloadPath: (id, token) =>
      `${API}/employee/badge-declarations/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  vt: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-700",
    downloadPath: (id, token) =>
      `${API}/employee/vt-declarations/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  nao_adesao: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-700",
    downloadPath: (id, token) =>
      `${API}/employee/nao-adesao/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  kit_integracao_matriz: {
    color: "bg-sky-100 text-sky-800 border-sky-200",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-700",
    downloadPath: (id, token) =>
      `${API}/employee/kit-documents/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  kit_integracao_filial: {
    color: "bg-sky-100 text-sky-800 border-sky-200",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-700",
    downloadPath: (id, token) =>
      `${API}/employee/kit-documents/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  kit_lgpd: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-700",
    downloadPath: (id, token) =>
      `${API}/employee/kit-documents/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  kit_ordem_servico: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-700",
    downloadPath: (id, token) =>
      `${API}/employee/kit-documents/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  kit_ficha_registro: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-700",
    downloadPath: (id, token) =>
      `${API}/employee/kit-documents/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
  // Termino de Contrato — composite recipient_id ``<docId>:<piece>``
  // (or ``<docId>:att:<attachmentId>`` for attachments).
  termino_comunicado: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-700",
    downloadPath: (id, token) => {
      const [docId] = String(id).split(":");
      return `${API}/employee/termino-contrato/${docId}/pdf`
        + `?token=${encodeURIComponent(token)}&piece=comunicado`;
    },
  },
  termino_trct: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-700",
    downloadPath: (id, token) => {
      const [docId] = String(id).split(":");
      return `${API}/employee/termino-contrato/${docId}/pdf`
        + `?token=${encodeURIComponent(token)}&piece=trct`;
    },
  },
  termino_protocolo: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-700",
    downloadPath: (id, token) => {
      const [docId] = String(id).split(":");
      return `${API}/employee/termino-contrato/${docId}/pdf`
        + `?token=${encodeURIComponent(token)}&piece=protocolo`;
    },
  },
  termino_carta: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-700",
    downloadPath: (id, token) => {
      const [docId] = String(id).split(":");
      return `${API}/employee/termino-contrato/${docId}/pdf`
        + `?token=${encodeURIComponent(token)}&piece=carta`;
    },
  },
  termino_anexo: {
    color: "bg-slate-100 text-slate-800 border-slate-200",
    iconBg: "bg-slate-100",
    iconColor: "text-slate-700",
    downloadPath: (id, token) => {
      // recipient_id format: "<docId>:att:<attachmentId>"
      const parts = String(id).split(":");
      const docId = parts[0];
      const attId = parts[2] || "";
      return `${API}/employee/termino-contrato/${docId}/attachment/${attId}`
        + `?token=${encodeURIComponent(token)}`;
    },
  },
  termino_externo: {
    color: "bg-slate-100 text-slate-800 border-slate-200",
    iconBg: "bg-slate-100",
    iconColor: "text-slate-700",
    downloadPath: (id, token) => {
      // recipient_id format: "<docId>:external"
      const [docId] = String(id).split(":");
      return `${API}/employee/termino-contrato/${docId}/external`
        + `?token=${encodeURIComponent(token)}`;
    },
  },
  banner_ack: {
    color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    iconBg: "bg-fuchsia-100",
    iconColor: "text-fuchsia-700",
    // Downloads the comunicado image (PNG) as evidence — recipient_id is
    // the banner id. Kept as image since banners are visual notices, not PDF.
    downloadPath: (id, token) =>
      `${API}/employee/banners/${id}/image?token=${encodeURIComponent(token)}`,
    // Force image mime type (overrides default application/pdf blob type).
    downloadMime: "image/png",
    downloadExt: "png",
  },
  document_receipt: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-700",
    downloadPath: (id, token) =>
      `${API}/employee/document-receipts/${id}/pdf?token=${encodeURIComponent(token)}`,
  },
};

const EmployeeMyDocuments = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState(null); // recipient_id of doc being downloaded

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee/my-documents`, {
        params: { token }, timeout: 20000,
      });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error("Não foi possível carregar seus documentos.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (doc) => {
    const meta = TYPE_META[doc.type];
    if (!meta) return;
    setDownloading(doc.recipient_id);
    try {
      const url = meta.downloadPath(doc.recipient_id, token);
      const r = await axios.get(url, {
        responseType: "blob", timeout: 60000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = doc.filename || `${doc.type}_${doc.signature_id || "doc"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
      toast.success("Download iniciado");
    } catch (err) {
      toast.error("Falha ao baixar o documento. Tente novamente.");
    } finally {
      setDownloading(null);
    }
  };

  const viewPdf = async (doc) => {
    const meta = TYPE_META[doc.type];
    if (!meta) return;
    setDownloading(doc.recipient_id);
    try {
      const url = meta.downloadPath(doc.recipient_id, token);
      const r = await axios.get(url, {
        responseType: "blob", timeout: 60000,
      });
      const blob = new Blob([r.data], { type: meta.downloadMime || "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error("Falha ao abrir o documento.");
    } finally {
      setDownloading(null);
    }
  };

  const filtered = items.filter((it) =>
    filter === "all" ? true : it.type === filter
  );

  const counts = items.reduce((acc, it) => {
    acc[it.type] = (acc[it.type] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="my-documents-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
        data-testid="my-documents-empty"
      >
        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 text-sm mb-1">
          Nenhum documento assinado ainda.
        </p>
        <p className="text-slate-400 text-xs">
          Ao assinar um Aviso de Férias, Recibo de Férias ou Kit Admissional,
          ele aparecerá aqui automaticamente.
        </p>
      </motion.div>
    );
  }

  const filterButton = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setFilter(key)}
      className={`px-3 py-1.5 text-xs sm:text-sm rounded-full border transition ${
        filter === key
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
      }`}
      data-testid={`my-documents-filter-${key}`}
    >
      {label}
      {key !== "all" && counts[key] ? (
        <span className="ml-1.5 opacity-80">({counts[key]})</span>
      ) : null}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="my-documents-list">
      {/* Filter Bar */}
      <div className="flex flex-wrap gap-2">
        {filterButton("all", `Todos (${items.length})`)}
        {counts.vacation_advance ? filterButton("vacation_advance", "Avisos de Férias") : null}
        {counts.vacation_notice ? filterButton("vacation_notice", "Recibos de Férias") : null}
        {counts.admission_kit ? filterButton("admission_kit", "Kit Admissional") : null}
        {counts.document_receipt ? filterButton("document_receipt", "Termos de Aceite") : null}
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {filtered.map((doc, idx) => {
          const meta = TYPE_META[doc.type] || TYPE_META.vacation_advance;
          const isBusy = downloading === doc.recipient_id;
          return (
            <motion.div
              key={`${doc.type}-${doc.recipient_id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              data-testid={`my-document-item-${doc.recipient_id}`}
            >
              <Card className="hover:shadow-md transition-shadow border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-11 h-11 rounded-lg ${meta.iconBg} flex items-center justify-center`}>
                      <FileText className={`w-5 h-5 ${meta.iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="outline" className={`text-[10px] sm:text-xs ${meta.color}`}>
                          {doc.type_label}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] sm:text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Assinado
                        </Badge>
                      </div>

                      <p className="text-sm font-medium text-slate-800 truncate" title={doc.filename}>
                        {doc.filename || "Documento assinado"}
                      </p>

                      <div className="mt-1 space-y-0.5 text-[11px] sm:text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className="w-3 h-3" />
                          <span>Assinado em {fmtDateTime(doc.signed_at)}</span>
                        </div>
                        {doc.signature_id ? (
                          <div className="flex items-center gap-1.5">
                            <ShieldCheck className="w-3 h-3" />
                            <span className="font-mono">{doc.signature_id}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewPdf(doc)}
                        disabled={isBusy}
                        data-testid={`my-document-view-${doc.recipient_id}`}
                      >
                        {isBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Ver</span>
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => downloadPdf(doc)}
                        disabled={isBusy}
                        data-testid={`my-document-download-${doc.recipient_id}`}
                      >
                        {isBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Baixar</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default EmployeeMyDocuments;
